'use client';

import Image, { ImageProps, StaticImageData } from 'next/image';
import React, { useEffect, useState, type CSSProperties } from 'react';
import { cn } from '../../../lib/utils';
import { useSanityHotspot, type SanityHotspotSource } from '@workspace/hooks';

export interface IAppImageProps extends Omit<ImageProps, 'src'> {
  src?: string | StaticImageData | null;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  sanityImage?: SanityHotspotSource;
  priority?: boolean;
  fetchPriority?: ImageProps['fetchPriority'];
  overlayGradient?: {
    direction?: string;
    startColor?: string;
    endColor?: string;
    opacity?: number;
  };
}

export function AppImage({
  src,
  alt,
  width = 100,
  height = 100,
  className,
  sanityImage,
  fill,
  overlayGradient,
  priority,
  fetchPriority,
  style,
  ...props
}: IAppImageProps) {
  const [isMobile, setIsMobile] = useState(false);
  const { className: hotspotClassName, style: hotspotStyle } = useSanityHotspot(sanityImage);

  useEffect(() => {
    const checkScreen = () => setIsMobile(window.innerWidth < 768);
    checkScreen();
    window.addEventListener('resize', checkScreen);
    return () => window.removeEventListener('resize', checkScreen);
  }, []);

  const { direction, startColor, endColor, opacity = 0.524 } = overlayGradient || {};

  const gradientStyle: React.CSSProperties = {
    background: `linear-gradient(${isMobile ? 'to bottom' : direction || 'to bottom'}, ${startColor}, ${endColor})`,
    opacity: src ? opacity : 1,
  };

  // Merge hotspot className and style with existing ones
  // Default to object-center if no hotspot className is provided
  const mergedClassName = cn('object-cover', hotspotClassName ?? 'object-center', className);
  type ImageStyle = React.ComponentProps<typeof Image>['style'];
  const mergedStyle: ImageStyle =
    style || hotspotStyle ? ({ ...(style as CSSProperties), ...(hotspotStyle ?? {}) } as ImageStyle) : undefined;

  return (
    <div className="w-full h-full">
      {src && (
        <Image
          src={src}
          alt={alt || 'Image'}
          {...(fill ? { fill } : { width: width || 100, height: height || 100 })}
          className={mergedClassName}
          style={mergedStyle}
          priority={priority}
          fetchPriority={fetchPriority}
          {...props}
        />
      )}
      <div className="absolute inset-0 pointer-events-none" style={gradientStyle} />
    </div>
  );
}
